import { teamBadgeIcon } from '@/views/settings/teams/teamIcon';
import {
  FAVICON_DESIGN_VERSION,
  renderTeamFavicon,
  teamBadgeColor,
} from './renderFavicon';

/**
 * Favicon for the tab's team: the app logo with a badge in the team's color
 * and icon, so tabs on different teams can be told apart.
 */
export const FAVICON_LINK_ID = 'app-favicon';
export const FAVICON_CACHE_NAME = 'team-favicons';

export const teamFaviconCacheKey = (team, origin = window.location.origin) => {
  const params = new URLSearchParams({
    org: String(team?.org_id ?? ''),
    team: String(team?.id ?? ''),
    icon: teamBadgeIcon(team),
    color: teamBadgeColor(team),
    design: FAVICON_DESIGN_VERSION,
  });
  return `${origin}/__team-favicon-cache__?${params.toString()}`;
};

// Cache API is missing in insecure contexts and can throw (quota, private
// mode); every failure falls through to drawing without the cache.
const openCache = async (cachesApi) => {
  if (!cachesApi?.open) return null;
  try {
    return await cachesApi.open(FAVICON_CACHE_NAME);
  } catch (e) {
    return null;
  }
};

const readCache = async (cache, key) => {
  try {
    const hit = await cache?.match(key);
    return hit ? await hit.blob() : null;
  } catch (e) {
    return null;
  }
};

const writeCache = async (cache, key, blob) => {
  try {
    await cache?.put(
      key,
      new Response(blob, { headers: { 'Content-Type': 'image/png' } })
    );
  } catch (e) {
    // a failed write only costs a redraw next time
  }
};

export const createTeamTab = ({
  doc = document,
  cachesApi = typeof caches === 'undefined' ? null : caches,
  render = (team) => renderTeamFavicon(team, doc),
  objectUrls = URL,
  origin = window.location.origin,
} = {}) => {
  let latestRequest = 0;
  let objectUrl = null;
  // The build injects several rel=icon links (svg, 16, 32). While a team icon
  // shows they are detached so the single team link is the only candidate.
  let defaultIconLinks = null;

  const revokeObjectUrl = () => {
    if (objectUrl) objectUrls.revokeObjectURL(objectUrl);
    objectUrl = null;
  };

  const showDefaultIcon = () => {
    doc.getElementById(FAVICON_LINK_ID)?.remove();
    if (defaultIconLinks) {
      defaultIconLinks.forEach((link) => doc.head.appendChild(link));
      defaultIconLinks = null;
    }
    revokeObjectUrl();
  };

  const showTeamIcon = (blob) => {
    if (!defaultIconLinks) {
      defaultIconLinks = Array.from(
        doc.querySelectorAll('link[rel~="icon"]')
      ).filter((link) => link.id !== FAVICON_LINK_ID);
      defaultIconLinks.forEach((link) => link.remove());
    }

    let link = doc.getElementById(FAVICON_LINK_ID);
    if (!link) {
      link = doc.createElement('link');
      link.id = FAVICON_LINK_ID;
      link.rel = 'icon';
      link.type = 'image/png';
      doc.head.appendChild(link);
    }

    const previousUrl = objectUrl;
    objectUrl = objectUrls.createObjectURL(blob);
    link.href = objectUrl;
    if (previousUrl) objectUrls.revokeObjectURL(previousUrl);
  };

  const getFaviconBlob = async (forTeam) => {
    const key = teamFaviconCacheKey(forTeam, origin);
    const cache = await openCache(cachesApi);
    const cached = await readCache(cache, key);
    if (cached) return cached;

    const { blob, cacheable } = await render(forTeam);
    if (cacheable) await writeCache(cache, key, blob);
    return blob;
  };

  /**
   * Shows the default icon until the team's is drawn. A slower earlier call
   * can never overwrite a later team or a clear().
   */
  const show = async (nextTeam) => {
    if (!nextTeam) return clear();

    const request = ++latestRequest;
    showDefaultIcon();

    try {
      const blob = await getFaviconBlob(nextTeam);
      if (request !== latestRequest) return;
      showTeamIcon(blob);
    } catch (e) {
      console.warn('Could not generate team favicon', e);
    }
  };

  const clear = () => {
    latestRequest += 1;
    showDefaultIcon();
  };

  return { show, clear };
};

let teamTab = null;
const instance = () => {
  if (!teamTab) teamTab = createTeamTab();
  return teamTab;
};

export const showTeamInTab = (team) => instance().show(team);
export const clearTeamInTab = () => instance().clear();
